import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import InactiveIcon from '~/icons/favorit.svg';
import ActiveIcon from '~/icons/favorit-active.svg';
import styles from './WishlistButton.module.scss';

interface WishlistButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
    isActive?: boolean;
    onDebouncedClick?(): void;
}

export const WishlistButton: React.FC<WishlistButtonProps> = ({
    className,
    isActive,
    onClick,
    onDebouncedClick,
    ...rest
}) => {
    const [isWishlisted, setIsWishlisted] = useState<boolean>(isActive ?? false);

    // Sync isWishlisted state with isActive prop from parent.
    useEffect(() => setIsWishlisted(isActive ?? false), [isActive]);

    // This ensures that we will only execute onDebouncedClick every 1ms.
    const debouncedOnClick = useCallback(
        debounce(() => onDebouncedClick?.(), 1000),
        [onDebouncedClick],
    );

    const onButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();

            setIsWishlisted((prevState) => !prevState);

            onClick?.(event);
            debouncedOnClick();
        },
        [debouncedOnClick, onClick],
    );

    return (
        <button
            className={clsx(styles.wishlist, className)}
            aria-label="Add to favorites"
            title="Add to favorites"
            {...rest}
            onClick={onButtonClick}
        >
            {isWishlisted ? (
                <ActiveIcon className={styles.icon} />
            ) : (
                <InactiveIcon className={styles.icon} />
            )}
        </button>
    );
};
